import path from "node:path";
import { execa } from "execa";
import { shutdownAnalytics } from "../common/analytics/lib.js";
import { printCriticalFailureToConsole } from "../common/output.js";
import {
  LintBinaryNotFoundError,
  resolveLintBinary,
} from "./resolve-binary.js";

export interface Arguments {
  dir: string;
  config?: string;
  format: string;
  failOn?: string;
  fix: boolean;
  lintBinary?: string;
}

/**
 * Exit codes, matching the linter's own contract so CI can tell the two apart:
 * 1 means the project has violations, 2 means the linter could not run.
 */
const EXIT_VIOLATIONS_FOUND = 1;
const EXIT_RUN_FAILED = 2;

export async function lint(argv: Arguments) {
  let binary: string;
  try {
    binary = resolveLintBinary({ override: argv.lintBinary });
  } catch (err) {
    if (err instanceof LintBinaryNotFoundError) {
      printCriticalFailureToConsole(err.message);
      await exit(EXIT_RUN_FAILED);
      return;
    }
    throw err;
  }

  const args = ["--dir", path.resolve(argv.dir), "--format", argv.format];
  if (argv.config) {
    args.push("--config", path.resolve(argv.config));
  }
  if (argv.failOn) {
    args.push("--fail-on", argv.failOn);
  }
  if (argv.fix) {
    args.push("--fix");
  }

  try {
    // The linter writes its own report — results to stdout, diagnostics to
    // stderr — so inherit the streams rather than buffering and re-printing.
    // That also leaves its color and TTY detection intact.
    await execa(binary, args, { stdio: "inherit" });
    return;
  } catch (cause) {
    const exitCode = childExitCode(cause);

    if (exitCode === undefined) {
      printCriticalFailureToConsole(
        `Could not run the linter at ${binary}.\n` +
          `${cause instanceof Error ? cause.message : String(cause)}`
      );
      await exit(EXIT_RUN_FAILED);
      return;
    }

    // Anything other than "violations found" means the linter itself failed.
    // Its diagnostic already went to stderr, but on its own that reads like a
    // lint result rather than a broken invocation.
    if (exitCode !== EXIT_VIOLATIONS_FOUND) {
      printCriticalFailureToConsole(
        `Lint failed to run (exit code ${exitCode}).`
      );
    }

    await exit(exitCode);
  }
}

/**
 * Exit with the linter's own status rather than throwing. Throwing would send
 * this through cli.ts's catch, which prints a redundant error and dumps the
 * help text on top of a report the user has already read. Matches how
 * `zuplo test` propagates a failing run.
 */
async function exit(code: number): Promise<never> {
  await shutdownAnalytics();
  process.exit(code);
}

/**
 * The child's exit status, or undefined when the process never ran. execa types
 * `exitCode` as a number, but a spawn failure — the file is not executable, or
 * is a binary for another architecture — produces an error without one.
 */
function childExitCode(err: unknown): number | undefined {
  if (err instanceof Error && "exitCode" in err) {
    const { exitCode } = err as { exitCode: unknown };
    return typeof exitCode === "number" ? exitCode : undefined;
  }
  return undefined;
}
